import { useState, FC, useCallback } from "react";
import { Button, toast } from "@altimateai/lego";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import {
  TagsInput,
  TagsInputLabel,
  TagsInputList,
  TagsInputInput,
  TagsInputItem,
  TagsInputClear,
} from "@altimateai/lego";
import { RefreshIcon } from "../../../../assets/icons";

interface EmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSendEmail: (emails: string[]) => Promise<void>;
  isLoading?: boolean;
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const EmailDialog: FC<EmailDialogProps> = ({
  open,
  onClose,
  onSendEmail,
  isLoading = false,
}) => {
  const [emails, setEmails] = useState<string[]>([]);

  const handleValidateEmail = useCallback((email: string): boolean => {
    return validateEmail(email);
  }, []);

  const handleSendEmail = async () => {
    const validEmails = emails.filter((email) => validateEmail(email));

    if (validEmails.length === 0) {
      return;
    }

    try {
      await onSendEmail(validEmails);
      toast({
        title: "Success",
        description: `Email sent to ${validEmails.length} recipient${
          validEmails.length > 1 ? "s" : ""
        }.`,
      });
      // Clear form on success
      setEmails([]);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
      console.error("Failed to send email:", error);
    }
  };

  const handleClose = () => {
    setEmails([]);
    onClose();
  };

  const validEmailCount = emails.filter((email) => validateEmail(email)).length;
  const hasInvalidEmails = emails.some((email) => !validateEmail(email));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="al-max-w-md">
        <DialogHeader>
          <DialogTitle>Email Response</DialogTitle>
        </DialogHeader>

        <div className="al-space-y-4">
          <TagsInput
            value={emails}
            onValueChange={(next) =>
              setEmails((prev) => {
                if (
                  prev.length > 0 &&
                  next.length === 1 &&
                  !prev.includes(next[0])
                ) {
                  return [...prev, next[0]];
                }
                return next;
              })
            }
            onValidate={handleValidateEmail}
            addOnPaste
            addOnTab
            delimiter=","
            className="al-w-full"
            disabled={isLoading}
          >
            <TagsInputLabel>Email Recipients</TagsInputLabel>
            <TagsInputList>
              {emails.map((email) => (
                <TagsInputItem
                  key={email}
                  value={email}
                  className={
                    validateEmail(email)
                      ? "al-bg-blue-100 al-text-blue-800 al-border-blue-200"
                      : "al-bg-red-100 al-text-red-800 al-border-red-200"
                  }
                >
                  {email}
                </TagsInputItem>
              ))}
              <TagsInputInput
                placeholder={
                  emails.length === 0 ? "Enter email addresses..." : ""
                }
                disabled={isLoading}
                aria-label="add emails"
              />
            </TagsInputList>
            <TagsInputClear asChild>
              <Button
                variant="outline"
                size="sm"
                className="al-flex al-items-center al-gap-2 al-mt-2"
                disabled={isLoading || emails.length === 0}
              >
                <RefreshIcon className="al-h-4 al-w-4" />
                Clear All
              </Button>
            </TagsInputClear>
          </TagsInput>

          <div className="al-space-y-2">
            <p className="al-text-xs al-text-gray-400">
              Press Enter, Tab, or comma to add an email. Use Backspace to
              remove emails.
            </p>
            {hasInvalidEmails && (
              <p className="al-text-xs al-text-red-600">
                Some email addresses are invalid and will be ignored.
              </p>
            )}
            {validEmailCount > 0 && (
              <div className="al-text-xs al-text-blue-400">
                Ready to send to {validEmailCount} recipient
                {validEmailCount !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Stack direction="row" justify="end" gap="sm">
            <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={validEmailCount === 0 || isLoading}
              aria-busy={isLoading}
            >
              {isLoading
                ? "Sending..."
                : `Send Email${validEmailCount > 1 ? "s" : ""}`}
            </Button>
          </Stack>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
